import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFetch } from "./useFetch";
import { ENDPOINTS } from "@/const/endpoints";

const FEED_PAGE_SIZE = 10;
const GUEST_LAST_SEEN_KEY = "apily_system_notifications_last_seen";

function readGuestLastSeen() {
  try {
    return window.localStorage.getItem(GUEST_LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

function writeGuestLastSeen(value) {
  try {
    window.localStorage.setItem(GUEST_LAST_SEEN_KEY, value);
  } catch {
    // localStorage unavailable (private mode, blocked) — unread state just won't persist.
  }
}

function hasUnread(items, lastSeenIso) {
  if (items.length === 0) return false;
  if (!lastSeenIso) return true;
  const lastSeenTime = new Date(lastSeenIso).getTime();
  return items.some((item) => new Date(item.createdAt).getTime() > lastSeenTime);
}

export function useNotificationsFeed() {
  const fetchClient = useFetch();
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [lastSeen, setLastSeen] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);

  const load = useCallback(() => {
    return fetchClient
      .get(ENDPOINTS.NOTIFICATIONS_FEED, {
        params: { page: 1, limit: FEED_PAGE_SIZE },
        skipNetworkErrorToast: true,
      })
      .then((res) => {
        const body = res.data || {};
        pageRef.current = 1;
        setSystemNotifications(body.systemNotifications || []);
        setHasMore(Boolean(body.hasMore));
        if (body.lastSeen !== null && body.lastSeen !== undefined) {
          setIsAuthorized(true);
          setLastSeen(body.lastSeen);
        } else {
          setIsAuthorized(false);
          setLastSeen(readGuestLastSeen());
        }
      })
      .catch(() => {
        // Keep whatever was loaded before; the bell simply won't update this cycle.
      })
      .finally(() => setLoading(false));
  }, [fetchClient]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = useCallback(() => {
    if (loadingMore) return Promise.resolve();
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    return fetchClient
      .get(ENDPOINTS.NOTIFICATIONS_FEED, {
        params: { page: nextPage, limit: FEED_PAGE_SIZE },
        skipNetworkErrorToast: true,
      })
      .then((res) => {
        const body = res.data || {};
        const incoming = body.systemNotifications || [];
        pageRef.current = nextPage;
        // A new notification published mid-session shifts the pages, so skip rows already shown.
        setSystemNotifications((prev) => {
          const seenIds = new Set(prev.map((item) => item._id));
          return [...prev, ...incoming.filter((item) => !seenIds.has(item._id))];
        });
        setHasMore(Boolean(body.hasMore));
      })
      .catch(() => {
        // Keep the current list and the button so the user can retry.
      })
      .finally(() => setLoadingMore(false));
  }, [fetchClient, loadingMore]);

  const unreadSystem = useMemo(
    () => hasUnread(systemNotifications, lastSeen),
    [systemNotifications, lastSeen],
  );

  const markSeen = useCallback(() => {
    const now = new Date().toISOString();
    setLastSeen(now);

    if (isAuthorized) {
      fetchClient
        .post(ENDPOINTS.NOTIFICATIONS_SEEN, {}, { skipNetworkErrorToast: true })
        .catch(() => {
          // Best-effort — unread already cleared optimistically on this device.
        });
      return;
    }

    writeGuestLastSeen(now);
  }, [fetchClient, isAuthorized]);

  return { systemNotifications, unreadSystem, loading, markSeen, hasMore, loadingMore, loadMore };
}
